import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  ColorPicker,
  Divider,
  Form,
  Input,
  Select,
  Spin,
  Switch,
  message,
} from "antd";
import { UploadFile } from "antd/lib";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // import the Quill styles
import {
  AddBlogAdmin,
  ListCateBlogAdmin,
  ShowBlogsAdmin,
  UpdateBlogAdmin,
} from "../../../network/admin";
import queryClient from "../../../services/react-query/queryClient";
import { checkStringInLanguage } from "../../../utils/utils";
import IMGUploader from "../../common/ui/IMGUploader";

type Props = { close: any; id?: number };
const modules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
    ["code-block"],
  ],
};

const formats = [
  "font",
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "color",
  "background",
  "script",
  "code-block",
];

const BlogForm = ({ id, close }: Props) => {
  const { data: blog, isLoading: isBlog } = useQuery(["blogs", id], () =>
    ShowBlogsAdmin(id, true)
  );
  const [imgs, setImgs] = useState<UploadFile[]>([]);
  const [lang, setLang] = useState<any>(blog?.data?.lang || "en");
  useEffect(() => {
    if (blog) {
      setLang(blog?.data?.lang);
      setImgs([
        {
          uid: blog?.data?.media || "",
          name: blog?.data?.media || "",
          url: blog?.data?.media || "",
          size: 100,
        },
      ]);
    }
  }, [id, blog]);
  const [backgroundColor, setBackGroundColor] = useState(
    blog?.data?.title_color || "#000000"
  );
  const [isActive, setIsActive] = useState(blog?.data?.is_active || true);
  const [form] = Form.useForm();

  const intl = useIntl();
  const { data: cates, isLoading } = useQuery(["categories"], () =>
    ListCateBlogAdmin({}, true)
  );
  console.log("Current language setting:", lang);
  console.log("Sample name object:", cates?.data[0]?.name[lang]);
  const options = cates?.data?.map((e: any) => {
    // Check if 'name' is an object and use 'lang' key if it is; otherwise use 'name' directly.
    const labelContent = typeof e.name === "object" ? e.name[lang] : e.name;

    // Ensure a default value if 'labelContent' is somehow undefined or null
    const label = labelContent || "sd";

    return {
      value: e.id,
      label: <div>{label}</div>,
    };
  });

  const [isAddingAnother, setIsAddingAnother] = useState(false);
  const resetForm = () => {
    // Reset your form fields
    form.resetFields();
  };
  const {
    mutate: addBlog,
    isLoading: isSubmitting,
    error: addSalonError,
  } = useMutation(AddBlogAdmin, {
    onSuccess: (res: any) => {
      if (res?.code === "ERR_BAD_REQUEST") {
        message.error(res?.response?.data?.message);
      } else {
        message.success("Blog Added Successfully");
        queryClient.invalidateQueries(["blogs"]);
        if (!isAddingAnother) {
          close(); // Close the modal only if not adding another blog
        } else {
          resetForm(); // Clear the form if adding another blog
        }
      }
    },
  });
  const { mutate: updateBlog, isLoading: isUpdateing } = useMutation(
    UpdateBlogAdmin,
    {
      onSuccess: (res: any) => {
        if (res?.code === "ERR_BAD_REQUEST") {
          message.error(res?.response?.data?.message);
        } else {
          message.success("Blog updated Successfully");
          queryClient.invalidateQueries(["blogs"]);
          if (!isAddingAnother) {
            close();
          } else {
            resetForm();
          }
        }
      },
    }
  );

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
    imgs?.length > 0
      ? message.error("Please fill all required fields.")
      : message.error("Please upload a main image.");
  };
  const setImgsForm = (newImgs: any) => {
    // Update your state
    setImgs(newImgs);

    // Manually set the form value
    form.setFieldsValue({
      main_image: newImgs.length > 0 ? newImgs[0] : undefined,
    });
  };
  console.log(imgs);
  const onFinish = (values: any) => {
    console.log(values);
    console.log(imgs);
    const formData = new FormData();
    formData.append("title", values?.title);
    formData.append("lang", values?.lang);
    formData.append("title_color", values?.title_color);
    formData.append("content", values?.content);
    formData.append("seo_title", values?.seo_title);
    formData.append("seo_description", values?.seo_description);
    formData.append("author", values?.author);
    formData.append("conclusion", values?.conclusion);
    formData.append("banner_title", values?.banner_title);
    formData.append("banner_button", values?.banner_button);
    formData.append("banner_description", values?.banner_description);
    formData.append("is_active", isActive ? "1" : "0");
    values?.categories?.forEach((e: any, i: number) =>
      formData.append(`categories[${i}]`, e)
    );
    if (imgs.length > 0 && imgs[0].originFileObj instanceof File) {
      formData.append("image", imgs[0].originFileObj);
    }

    id ? updateBlog({ id, data: formData }) : addBlog(formData);
  };
  return id && isBlog ? (
    <Spin className="flex items-center justify-center" size="large" />
  ) : (
    <div className="p-6 max-lg:p-3 min-w-[50%]">
      <h4 className="grid items-center justify-center my-4 mb-8 text-2xl font-semibold text-color-primary">
        {id ? "Edit Blog" : "New Blog"}{" "}
      </h4>
      <Form
        name="blog"
        onFinishFailed={onFinishFailed}
        layout={"vertical"}
        className="w-full grid-cols-1 gap-4 gap-y-0 blog-form lg:grid lg:grid-cols-2"
        initialValues={{
          main_image: blog?.data?.media || "",
          lang: blog?.data?.lang || lang || "en",
          title: blog?.data?.title || "",
          title_color: blog?.data?.title_color || "#000000",
          content: blog?.data?.content || "",
          categories: blog?.data?.categories?.map((e: any) => e?.id) || [],
          author: blog?.data?.user?.email || "",
          seo_title: blog?.data?.seo_title || "",
          seo_description: blog?.data?.seo_description || "",
          banner_title: blog?.data?.banner_title || "",
          banner_description: blog?.data?.banner_description || "",
          banner_button: blog?.data?.banner_button || "",
          conclusion: blog?.data?.conclusion || "",
          isActive: blog?.data?.is_active || true,
        }}
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Language
            </div>
          }
          name={"lang"}
          className="col-span-2"
          rules={[
            {
              required: true,
              message: "Please select a language",
            },
          ]}
          required={false}
          initialValue={lang}
        >
          <Select
            onChange={(e) => setLang(e)}
            value={lang || "en"}
            options={[
              { value: "ar", label: "Arabic" },
              { value: "en", label: "English" },
            ]}
          />
        </Form.Item>
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Main Image
            </div>
          }
          name={"main_image"}
          className="col-span-2"
          rules={[
            {
              required: true,
              message: "Please upload an Main Image",
            },
          ]}
          required={false}
        >
          <IMGUploader
            maxCount={1}
            fileList={imgs}
            setFileList={setImgsForm}
            listType="picture-card"
            imgValidation
            name="imgs"
            className="!mb-4 !w-full"
          />
        </Form.Item>
        <Divider className="col-span-2 mt-0" />
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Main Title
            </div>
          }
          name={"title"}
          className="h-fit"
          rules={[
            { required: true, message: "Title" },
            {
              validator: (_, value) => {
                if (value) {
                  if (checkStringInLanguage(value, lang)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      new Error(
                        lang === "en"
                          ? intl.formatMessage({
                              id: "service_name_english_error",
                            })
                          : intl.formatMessage({
                              id: "service_name_arabic_error",
                            })
                      )
                    );
                  }
                }
              },
            },
          ]}
          required={false}
        >
          <Input placeholder="Title" type="text" />
        </Form.Item>
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Main Title Color
            </div>
          }
          name={"title_color"}
          className=""
          rules={[{ required: true, message: "Title Color" }]}
          required={false}
        >
          <ColorPicker
            value={backgroundColor}
            onChange={(color) => {
              setBackGroundColor(color.toHexString());
            }}
            className="flex items-center justify-start w-full px-2"
            showText={(colorObj) => (
              <div className="text-[14px] min-w-[180px] w-full whitespace-nowrap  gap-4">
                <span>
                  {backgroundColor ||
                    intl.formatMessage({ id: "choose_color" })}
                </span>
              </div>
            )}
          />
        </Form.Item>
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Content
            </div>
          }
          name={"content"}
          className="col-span-2 longBodyQuill"
          rules={[{ required: true, message: "Content" }]}
          required={false}
        >
          <ReactQuill
            className="quill-editor"
            formats={formats}
            modules={modules}
            theme="snow"
          />
        </Form.Item>
        <Divider className="col-span-2 mt-0" />
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Category
            </div>
          }
          name={"categories"}
          className=""
          rules={[{ required: true, message: "Category" }]}
          required={false}
        >
          <Select
            mode="multiple"
            defaultValue={"cate1"}
            placeholder="Category"
            className=""
            options={options}
          />
        </Form.Item>
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">Author</div>
          }
          name={"author"}
          className=""
          rules={[{ required: true, message: "Author" }]}
          required={false}
        >
          <Input placeholder="ex.ahmed@x.com" type="text" />
        </Form.Item>
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              SEO Title
            </div>
          }
          name={"seo_title"}
          className=""
          rules={[
            { required: true, message: "SEO Title" },
            {
              validator: (_, value) => {
                if (value) {
                  if (checkStringInLanguage(value, lang)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      new Error(
                        lang === "en"
                          ? intl.formatMessage({
                              id: "service_name_english_error",
                            })
                          : intl.formatMessage({
                              id: "service_name_arabic_error",
                            })
                      )
                    );
                  }
                }
              },
            },
          ]}
          required={false}
        >
          <Input placeholder="SEO Title" type="text" />
        </Form.Item>
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              SEO Description
            </div>
          }
          name={"seo_description"}
          className=""
          rules={[
            { required: true, message: "SEO Description" },
            {
              validator: (_, value) => {
                if (value) {
                  if (checkStringInLanguage(value, lang)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      new Error(
                        lang === "en"
                          ? intl.formatMessage({
                              id: "service_name_english_error",
                            })
                          : intl.formatMessage({
                              id: "service_name_arabic_error",
                            })
                      )
                    );
                  }
                }
              },
            },
          ]}
          required={false}
        >
          <Input placeholder="SEO Description" type="text" />
        </Form.Item>
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Conclusion{" "}
              <sub className="text-xs text-[#9999A7] mx-1">(Optional)</sub>
            </div>
          }
          name={"conclusion"}
          className="col-span-2 medBodyQuill"
          rules={[{ required: false, message: "Conclusion" }]}
          required={false}
        >
          <ReactQuill
            className="quill-editor"
            formats={formats}
            modules={modules}
            theme="snow"
          />
        </Form.Item>
        <Divider className="col-span-2" />
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Banner Title
            </div>
          }
          name={"banner_title"}
          className="h-fit"
          rules={[
            { required: true, message: "Banner Title" },
            {
              validator: (_, value) => {
                if (value) {
                  if (checkStringInLanguage(value, lang)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      new Error(
                        lang === "en"
                          ? intl.formatMessage({
                              id: "service_name_english_error",
                            })
                          : intl.formatMessage({
                              id: "service_name_arabic_error",
                            })
                      )
                    );
                  }
                }
              },
            },
          ]}
          required={false}
        >
          <Input placeholder="Banner Title" type="text" />
        </Form.Item>
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Banner Button
            </div>
          }
          name={"banner_button"}
          className=""
          rules={[
            { required: true, message: "Banner Button" },
            {
              validator: (_, value) => {
                if (value) {
                  if (checkStringInLanguage(value, lang)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      new Error(
                        lang === "en"
                          ? intl.formatMessage({
                              id: "service_name_english_error",
                            })
                          : intl.formatMessage({
                              id: "service_name_arabic_error",
                            })
                      )
                    );
                  }
                }
              },
            },
          ]}
          required={false}
        >
          <Input placeholder="Banner Button" type="text" />
        </Form.Item>{" "}
        <Form.Item
          label={
            <div className="flex items-start justify-start text-sm">
              Banner Description
            </div>
          }
          name={"banner_description"}
          className="col-span-2"
          rules={[{ required: true, message: "Banner Description" }]}
          required={false}
        >
          <ReactQuill
            className="quill-editor"
            formats={formats}
            modules={modules}
            theme="snow"
          />
        </Form.Item>
        <Form.Item name={"is active"} className="col-span-2" required={false}>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-start justify-start w-full text-base font-semibold capitalize text-color-primary">
              is active
            </div>
            <Switch
              value={isActive}
              onChange={() => setIsActive((e: any) => !e)}
              className="flex items-center justify-end"
            />
          </div>
        </Form.Item>
        <div className="flex items-center justify-between w-full col-span-2 gap-4 mt-8">
          <Button
            type="primary"
            htmlType="submit"
            block
            className="w-full"
            loading={isSubmitting || isUpdateing}
            onClick={() => setIsAddingAnother(false)}
          >
            Save and publish
          </Button>{" "}
          {!!id || (
            <Button
              type="primary"
              htmlType="submit"
              className="w-full !bg-[#595FBC] hover:!bg-[#8389ED]"
              block
              loading={isSubmitting || isUpdateing}
              onClick={() => setIsAddingAnother(true)}
            >
              Save and add another
            </Button>
          )}
          <Button
            onClick={close}
            type="default"
            className="!w-[15%]"
            htmlType="button"
            block
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BlogForm;
